// Smart Algorithm Engine for Skincare Routine Generation

// Product-to-Ingredient Inference System
export const productInferenceMap = {
  // Cleansers
  "gel-cleanser": ["salicylic-acid", "glycolic-acid", "tea-tree"],
  "cream-cleanser": ["ceramides", "glycerin", "peptides"],
  "oil-cleanser": ["jojoba-oil", "squalane", "vitamin-e"],
  "micellar-water": ["glycerin", "panthenol", "allantoin"],
  
  // Moisturizers
  "light-moisturizer": ["hyaluronic-acid", "niacinamide", "glycerin"],
  "heavy-moisturizer": ["ceramides", "shea-butter", "peptides"],
  "spf-moisturizer": ["zinc-oxide", "titanium-dioxide", "antioxidants"],
  
  // Serums
  "brightening-serum": ["vitamin-c", "niacinamide", "arbutin", "kojic-acid"],
  "hydrating-serum": ["hyaluronic-acid", "glycerin", "ceramides", "panthenol"],
  "anti-aging-serum": ["retinol", "peptides", "vitamin-c", "bakuchiol"],
  "acne-serum": ["salicylic-acid", "benzoyl-peroxide", "niacinamide", "tea-tree"],
  
  // Treatments
  "retinol-treatment": ["retinol", "retinyl-palmitate", "bakuchiol"],
  "acid-treatment": ["glycolic-acid", "lactic-acid", "salicylic-acid"],
  "spot-treatment": ["benzoyl-peroxide", "salicylic-acid", "sulfur"],
  
  // Others
  "sunscreen": ["zinc-oxide", "titanium-dioxide", "avobenzone"],
  "face-oil": ["squalane", "jojoba-oil", "rosehip-oil", "vitamin-e"],
  "eye-cream": ["caffeine", "peptides", "hyaluronic-acid", "vitamin-k"]
};

// Ingredient Compatibility Matrix
export const conflictMatrix = {
  "retinol": ["aha", "bha", "benzoyl-peroxide", "high-vitamin-c", "glycolic-acid", "lactic-acid"],
  "vitamin-c": ["retinol", "aha", "bha", "benzoyl-peroxide"],
  "glycolic-acid": ["retinol", "salicylic-acid", "benzoyl-peroxide", "vitamin-c"],
  "lactic-acid": ["retinol", "salicylic-acid", "benzoyl-peroxide", "vitamin-c"],
  "salicylic-acid": ["retinol", "glycolic-acid", "lactic-acid", "benzoyl-peroxide"],
  "benzoyl-peroxide": ["retinol", "vitamin-c", "aha", "bha", "glycolic-acid", "salicylic-acid"],
  "bakuchiol": [], // Gentle retinol alternative, no major conflicts
  "tea-tree": ["benzoyl-peroxide"] // Can be drying when combined
};

// Ingredient Synergies
export const synergies = {
  "hyaluronic-acid": ["ceramides", "niacinamide", "vitamin-c", "peptides", "glycerin"],
  "niacinamide": ["hyaluronic-acid", "ceramides", "retinol", "vitamin-c", "salicylic-acid"],
  "ceramides": ["hyaluronic-acid", "niacinamide", "peptides", "glycerin"],
  "vitamin-c": ["vitamin-e", "ferulic-acid", "hyaluronic-acid", "niacinamide"],
  "retinol": ["niacinamide", "ceramides", "hyaluronic-acid", "peptides"],
  "peptides": ["hyaluronic-acid", "ceramides", "retinol", "niacinamide"],
  "glycerin": ["hyaluronic-acid", "ceramides", "niacinamide"],
  "squalane": ["hyaluronic-acid", "ceramides", "vitamin-c", "retinol"]
};

// Ingredient Properties and Benefits
export const ingredientProfiles = {
  "hyaluronic-acid": {
    name: "Hyaluronic Acid",
    type: "hydrator",
    benefits: ["intense hydration", "plumping", "moisture retention"],
    suitableFor: ["all skin types"],
    timeOfDay: ["morning", "evening"],
    personality: "The moisture magnet - holds 1000x its weight in water"
  },
  "niacinamide": {
    name: "Niacinamide",
    type: "multi-tasker",
    benefits: ["oil control", "pore minimizing", "brightening", "barrier repair"],
    suitableFor: ["oily", "combination", "sensitive"],
    timeOfDay: ["morning", "evening"],
    personality: "The peacekeeper - calms inflammation and balances oil"
  },
  "retinol": {
    name: "Retinol",
    type: "anti-aging",
    benefits: ["cell turnover", "collagen production", "fine line reduction"],
    suitableFor: ["normal", "dry", "combination"],
    timeOfDay: ["evening"],
    personality: "The renovator - rebuilds skin from within, but needs patience"
  },
  "vitamin-c": {
    name: "Vitamin C",
    type: "antioxidant",
    benefits: ["brightening", "antioxidant protection", "collagen support"],
    suitableFor: ["all skin types"],
    timeOfDay: ["morning"],
    personality: "The brightener - your daily dose of glow and protection"
  },
  "salicylic-acid": {
    name: "Salicylic Acid",
    type: "exfoliant",
    benefits: ["pore clearing", "acne treatment", "oil control"],
    suitableFor: ["oily", "combination", "acne-prone"],
    timeOfDay: ["evening"],
    personality: "The deep cleaner - dives into pores to clear congestion"
  },
  "ceramides": {
    name: "Ceramides",
    type: "barrier repair",
    benefits: ["barrier strengthening", "moisture retention", "sensitivity reduction"],
    suitableFor: ["dry", "sensitive", "damaged barrier"],
    timeOfDay: ["morning", "evening"],
    personality: "The protector - rebuilds your skin's natural defense system"
  }
};

// Concern-to-Ingredient Mapping
export const concernSolutions = {
  "dryness": {
    primary: ["hyaluronic-acid", "ceramides", "glycerin", "squalane"],
    secondary: ["peptides", "niacinamide"],
    avoid: ["alcohol", "harsh-sulfates", "over-exfoliation"]
  },
  "oily-pores": {
    primary: ["niacinamide", "salicylic-acid", "zinc"],
    secondary: ["hyaluronic-acid", "ceramides"],
    avoid: ["heavy-oils", "comedogenic-ingredients"]
  },
  "acne": {
    primary: ["salicylic-acid", "benzoyl-peroxide", "niacinamide"],
    secondary: ["tea-tree", "zinc", "azelaic-acid"],
    avoid: ["heavy-oils", "comedogenic-ingredients", "over-exfoliation"]
  },
  "aging": {
    primary: ["retinol", "peptides", "vitamin-c"],
    secondary: ["hyaluronic-acid", "ceramides", "antioxidants"],
    avoid: ["harsh-scrubs", "alcohol", "over-exfoliation"]
  },
  "dark-spots": {
    primary: ["vitamin-c", "niacinamide", "arbutin", "kojic-acid"],
    secondary: ["retinol", "azelaic-acid", "licorice-extract"],
    avoid: ["harsh-scrubs", "picking", "sun-exposure"]
  },
  "dullness": {
    primary: ["vitamin-c", "glycolic-acid", "lactic-acid"],
    secondary: ["niacinamide", "hyaluronic-acid"],
    avoid: ["over-cleansing", "harsh-scrubs"]
  },
  "sensitivity": {
    primary: ["ceramides", "niacinamide", "centella-asiatica"],
    secondary: ["hyaluronic-acid", "allantoin", "panthenol"],
    avoid: ["fragrances", "essential-oils", "harsh-acids", "alcohol"]
  },
  "texture": {
    primary: ["salicylic-acid", "glycolic-acid", "lactic-acid"],
    secondary: ["niacinamide", "retinol", "enzymes"],
    avoid: ["harsh-scrubs", "over-exfoliation"]
  }
};

// Compatibility Checker Function
export const checkCompatibility = (ingredient1, ingredient2) => {
  const conflicts1 = conflictMatrix[ingredient1] || [];
  const conflicts2 = conflictMatrix[ingredient2] || [];
  
  const hasConflict = conflicts1.includes(ingredient2) || conflicts2.includes(ingredient1);
  
  const synergies1 = synergies[ingredient1] || [];
  const synergies2 = synergies[ingredient2] || [];
  
  const hasSynergy = synergies1.includes(ingredient2) || synergies2.includes(ingredient1);
  
  return {
    compatible: !hasConflict,
    synergistic: hasSynergy,
    conflict: hasConflict,
    recommendation: hasConflict ? 'separate' : hasSynergy ? 'combine' : 'neutral'
  };
};

// Routine Conflict Detection
export const detectRoutineConflicts = (morningIngredients, eveningIngredients) => {
  const conflicts = [];
  const allIngredients = [...morningIngredients, ...eveningIngredients];
  
  // Check for same-day conflicts
  for (let i = 0; i < allIngredients.length; i++) {
    for (let j = i + 1; j < allIngredients.length; j++) {
      const compatibility = checkCompatibility(allIngredients[i], allIngredients[j]);
      if (compatibility.conflict) {
        conflicts.push({
          ingredient1: allIngredients[i],
          ingredient2: allIngredients[j],
          severity: 'high',
          solution: 'Use on alternate days or separate morning/evening'
        });
      }
    }
  }
  
  return conflicts;
};

// Generate Ingredient Recommendations for Concerns
export const getIngredientsForConcerns = (concerns) => {
  const recommendations = [];
  
  concerns.forEach(concern => {
    const solution = concernSolutions[concern.concern];
    if (solution) {
      recommendations.push({
        concern: concern.concern,
        priority: concern.priority,
        primaryIngredients: solution.primary,
        secondaryIngredients: solution.secondary,
        ingredientsToAvoid: solution.avoid
      });
    }
  });
  
  return recommendations;
};
